var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as Yup from "yup";
import { validateMobileNumberServerSide } from "../Webshop/services/MobileNumberService";
import { validateSwishishPid } from "../utils";
import { POSTAL_CODE_MAX_LENGTH, POSTAL_CODE_MIN_LENGTH, validateCity, validatePostalCode, } from "./postalAddressValidation";
const { locale } = window.NTLF_CONFIG.portal;
export const getYUPValidators = (t) => {
    const illegalCharacters = "(?!.*[@*•●])";
    const postalCode = (allowEmpty) => Yup.string()
        .trim()
        .test("postal-code", function (value) {
        if (allowEmpty && !value) {
            return true;
        }
        const problem = validatePostalCode(value !== null && value !== void 0 ? value : "", locale.country.twoLetterCode);
        if (problem === null) {
            return true;
        }
        switch (problem.kind) {
            case "format":
                return this.createError({
                    message: problem.example === null
                        ? t("Invalid postal code")
                        : t("Input format: %s", {
                            postProcess: "sprintf",
                            sprintf: [problem.example],
                        }),
                });
            case "needsDigit":
                return this.createError({
                    message: t("Must contain at least one digit"),
                });
            case "characters":
                return this.createError({
                    message: t("Digits and/or letters, %d to %d characters.", {
                        postProcess: "sprintf",
                        sprintf: [POSTAL_CODE_MIN_LENGTH, POSTAL_CODE_MAX_LENGTH],
                    }),
                });
        }
    });
    return {
        FULL_NAME: Yup.string()
            .trim()
            .matches(new RegExp(`^${illegalCharacters}.*\\S{2,}.*\\s\\S{2,}.*$`), t("Minimum two parts, only characters allowed")),
        NAME_PART: Yup.string()
            .trim()
            .matches(new RegExp(`^${illegalCharacters}.*\\S{2,}.*$`), t("Only characters allowed")),
        ADDRESS: Yup.string()
            .trim()
            .matches(new RegExp(`^${illegalCharacters}.*$`), t("Special characters are not allowed.")),
        POSTAL_CODE: postalCode(false),
        // For forms where the postal code is optional
        POSTAL_CODE_OR_EMPTY: postalCode(true),
        // The rules from before EPMS-2505, kept for the checkout during peak
        // season. Remove them once the rules above have been proven in web
        // registration and MyAccount, and the checkout uses those instead.
        LEGACY_POSTAL_CODE: Yup.string()
            .trim()
            .matches(new RegExp(`^${illegalCharacters}[0-9A-z\\s\\-]{3,10}$`), t("Digits and/or letters, %d to %d characters.", {
            postProcess: "sprintf",
            sprintf: [3, 10],
        })),
        LEGACY_CITY: Yup.string()
            .trim()
            .matches(new RegExp(`^${illegalCharacters}.*$`), t("Special characters are not allowed.")),
        CITY: Yup.string()
            .trim()
            .test("city", function (value) {
            const problem = validateCity(value !== null && value !== void 0 ? value : "", locale.country.twoLetterCode);
            if (problem === null) {
                return true;
            }
            switch (problem.kind) {
                case "needsLetter":
                    return this.createError({
                        message: t("Must contain at least one letter"),
                    });
                case "characters":
                    return this.createError({
                        message: t("Special characters are not allowed."),
                    });
            }
        }),
        PASSWORD: Yup.string().matches(/.{6,}/, t("The passwords must be atleast 6 characters long")),
        MOBILE_PHONE_WITH_SERVER_VALIDATION: Yup.string()
            .matches(/^\+{0,1}\d{7,16}$/, t("%d-%d digits", { postProcess: "sprintf", sprintf: [7, 14] }))
            .test("is-valid-mobile", t("Invalid mobile number"), (value) => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            // Empty value should be handled by "required"
            if (value === "" || typeof value === "undefined") {
                return true;
            }
            return (_a = (yield validateMobileNumberServerSide(value))) !== null && _a !== void 0 ? _a : false;
        }))
            .transform((value) => value.replace(/[\s()-]+/g, "")),
        EMAIL_ADDRESS: Yup.string()
            .trim()
            .email(t("username@example.com"))
            // Don't allow user@example, it's confusing for the user when
            // buttons that wait for validation suddenly get enabled
            .matches(/.+@.+\..+$/, t("username@example.com")),
        // This need to support different languages:
        PERSONAL_ID_NUMBER_WITH_MIN_AGE: Yup.string()
            .test(function (value) {
            const { path, createError } = this;
            if (!value) {
                if (locale.country.twoLetterCode === "SE") {
                    return createError({
                        path,
                        message: t("Input format: %s", {
                            postProcess: "sprintf",
                            sprintf: ["YYMMDD-XXXX"],
                        }),
                    });
                }
                return createError({
                    path,
                    message: t("Invalid personal id number"),
                });
            }
            if (value === "0000") {
                return true;
            }
            if (locale.country.twoLetterCode === "SE") {
                const result = verifySwedishPid(value, t);
                if (result === true) {
                    return true;
                }
                else {
                    return createError({ path, message: result });
                }
            }
            else if (locale.country.twoLetterCode === "NO") {
                if (value.match(/^\d{11}$/)) {
                    return true;
                }
            }
            // We don't really have a check here?
            else if (value.length > 3) {
                return true;
            }
            return createError({ path, message: t("Invalid personal id number") });
        })
            .test("test-personal-number-age", t("You need to be at least 18 year to order"), (value) => {
            if (!value) {
                return false;
            }
            if (locale.country.twoLetterCode === "SE") {
                const res = validateSwishishPid(value);
                if (!res) {
                    return false;
                }
                else if (typeof res === "object" && res.age < 18) {
                    return false;
                }
            }
            return true;
        }),
    };
};
function verifySwedishPid(number, t) {
    number = number.replace(/-/g, "");
    if (number.match(/^\d{10}$/)) {
        let n = 2;
        // Kontrollsum
        let sum = 0;
        for (let i = 0; i < 9; i++) {
            let tmp = parseInt(number[i]) * n;
            tmp > 9 ? (sum += 1 + (tmp % 10)) : (sum += tmp);
            n == 2 ? (n = 1) : (n = 2);
        }
        const total = sum + parseInt(number[9]);
        const valid = !(total % 10);
        if (valid) {
            return true;
        }
        return t("Input format: %s", {
            postProcess: "sprintf",
            sprintf: ["YYMMDD-XXXX"],
        });
    }
    else {
        return t("Input format: %s", {
            postProcess: "sprintf",
            sprintf: ["YYMMDD-XXXX"],
        });
    }
}
